import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  CheckCircle, Crown, Rocket, Buildings, CreditCard,
  ArrowRight, SpinnerGap, Receipt, ArrowUp, ArrowDown,
  Warning, Hourglass, Prohibit, Stop, X
} from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';

const PLAN_ICONS = { basic: Buildings, pro: Rocket, enterprise: Crown };
const PLAN_COLORS = { basic: '#7367F0', pro: '#FF9F43', enterprise: '#28C76F' };

const BILLING_STATUS_CFG = {
  active:             { label: 'Active',          className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', Icon: CheckCircle },
  trialing:           { label: 'Trial',           className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',           Icon: Hourglass   },
  pending_payment:    { label: 'Pending Payment', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: CreditCard  },
  past_due:           { label: 'Payment Failed',  className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Warning     },
  unpaid:             { label: 'Unpaid',          className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Warning     },
  trial_expired:      { label: 'Trial Expired',   className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Prohibit    },
  canceled:           { label: 'Canceled',        className: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20',           Icon: Prohibit    },
  canceling:          { label: 'Canceling',       className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: Hourglass   },
  incomplete:         { label: 'Incomplete',      className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: Warning     },
  incomplete_expired: { label: 'Expired',         className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Prohibit    },
};

export default function TenantBillingPage() {
  const { apiCall } = useAuth();
  const [params] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [pollingSessionId, setPollingSessionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const sessionId = params.get('session_id');
    const status = params.get('status');
    if (sessionId && status === 'success') {
      setPollingSessionId(sessionId);
      pollStatus(sessionId, 0);
    } else if (status === 'cancelled') {
      setPaymentStatus({ type: 'cancelled', message: 'Payment was cancelled.' });
    }
  }, [params]);

  async function loadData() {
    try {
      const [billingRes, plansRes] = await Promise.all([
        apiCall('get', '/billing/my-plan'),
        apiCall('get', '/billing/plans')
      ]);
      setBilling(billingRes.data);
      setPlans(plansRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function handleCheckout(planId) {
    setCheckoutLoading(planId);
    try {
      // If tenant already has an active Stripe subscription, change plan in-place (proration).
      if (billing?.has_stripe_customer && billing?.subscription_status &&
          ['active', 'trialing', 'past_due'].includes(billing.subscription_status)) {
        const res = await apiCall('post', '/billing/change-plan', { plan_id: planId });
        if (res.data?.success) {
          const eff = res.data.effective === 'immediate'
            ? 'Plan upgraded — prorated charge applied to your card.'
            : `Downgrade scheduled — changes apply at your next renewal.`;
          setPaymentStatus({ type: 'success', message: eff });
          loadData();
        }
        return;
      }
      // Otherwise → first-time subscription → Stripe Checkout
      const res = await apiCall('post', '/billing/my-checkout', { plan_id: planId, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update plan'); }
    finally { setCheckoutLoading(null); }
  }

  async function handleManageSubscription() {
    try {
      const res = await apiCall('post', '/billing/portal', { return_url: window.location.href });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) { alert(e.response?.data?.detail || 'Unable to open subscription portal.'); }
  }

  async function handleCancelSubscription() {
    setCancelLoading(true);
    try {
      await apiCall('post', '/billing/cancel-subscription', {});
      setPaymentStatus({ type: 'success', message: 'Your subscription has been canceled. You can re-subscribe anytime from this page.' });
      setShowCancelDialog(false);
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Unable to cancel subscription. Please try again or contact support.');
    } finally { setCancelLoading(false); }
  }

  async function pollStatus(sessionId, attempts) {
    if (attempts >= 5) { setPaymentStatus({ type: 'timeout', message: 'Payment verification timed out. It may still be processing.' }); setPollingSessionId(null); return; }
    try {
      const res = await apiCall('get', `/billing/my-checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') { setPaymentStatus({ type: 'success', message: 'Payment successful! Your plan has been upgraded.' }); setPollingSessionId(null); loadData(); return; }
      if (res.data.status === 'expired') { setPaymentStatus({ type: 'expired', message: 'Payment session expired.' }); setPollingSessionId(null); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch { setPaymentStatus({ type: 'error', message: 'Error verifying payment.' }); setPollingSessionId(null); }
  }

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!billing) return <div className="p-6 text-muted-foreground">Unable to load billing information.</div>;

  const currentPlanId = billing.current_plan;
  const currentPlanOrder = plans.findIndex(p => p.plan_id === currentPlanId);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="tenant-billing-page">
      {/* Status Banners */}
      {paymentStatus && (
        <div className={`p-4 rounded-lg border ${paymentStatus.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'}`}>
          <div className="flex items-center gap-2">{paymentStatus.type === 'success' && <CheckCircle size={20} weight="fill" />}<span className="font-medium">{paymentStatus.message}</span></div>
        </div>
      )}
      {pollingSessionId && (
        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 flex items-center gap-2">
          <SpinnerGap size={20} className="animate-spin" /><span className="font-medium">Verifying payment...</span>
        </div>
      )}

      {/* Canceling banner — subscription canceled / scheduled to cancel */}
      {(billing.cancel_at_period_end || ['canceled', 'canceling'].includes(billing.subscription_status)) && !billing.is_locked && (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 flex items-start gap-3" data-testid="subscription-canceling-banner">
          <Prohibit size={22} weight="duotone" className="shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold">Your subscription has been canceled</p>
            <p className="text-xs mt-0.5">
              Access to projects, users, analytics and support is paused. Choose a plan below to re-subscribe and restore everything.
            </p>
          </div>
        </div>
      )}

      {billing.is_locked && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300" data-testid="subscription-locked-banner">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-semibold">Your subscription is {billing.subscription_status || 'inactive'}.</p>
              <p className="text-xs mt-0.5">Re-subscribe to a plan to regain access to projects, measurements and reporting.</p>
            </div>
          </div>
        </div>
      )}
      {billing.subscription_status === 'past_due' && !billing.is_locked && (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300">
          <p className="font-semibold">Payment failed.</p>
          <p className="text-xs mt-0.5">Please update your card via <button onClick={handleManageSubscription} className="underline font-semibold">Manage Subscription</button>.</p>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold" data-testid="tenant-billing-heading">Billing & Subscription</h1>
          <p className="text-muted-foreground text-sm mt-1">{billing.organization} — Manage your subscription plan</p>
        </div>
        {billing.has_stripe_customer && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={handleManageSubscription} className="rounded-md" data-testid="manage-subscription-btn">
              <CreditCard size={16} className="mr-2" />Manage Subscription
            </Button>
            {!billing.cancel_at_period_end && !['canceled', 'canceling'].includes(billing.subscription_status) && !billing.is_locked && (
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(true)}
                className="rounded-md border-red-500/40 text-red-600 hover:bg-red-500/10 hover:text-red-700"
                data-testid="cancel-subscription-btn"
              >
                <Stop size={16} className="mr-2" weight="fill" />Cancel Subscription
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Cancel confirmation dialog */}
      <Dialog open={showCancelDialog} onOpenChange={(v) => !cancelLoading && setShowCancelDialog(v)}>
        <DialogContent className="max-w-md" data-testid="cancel-subscription-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Prohibit size={22} weight="duotone" className="text-red-600" />
              Cancel your subscription?
            </DialogTitle>
            <DialogDescription>
              This will immediately pause access to <strong>Projects</strong>, <strong>Users</strong>, <strong>Analytics</strong> and <strong>Support</strong>. You and your team will only be able to see <strong>Dashboard</strong>, <strong>Billing</strong>, and <strong>Invoices</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-md bg-muted/50 text-xs text-muted-foreground space-y-1">
            <p>• Your data is preserved and will be restored if you re-subscribe.</p>
            <p>• Stripe will not bill you on the next renewal date.</p>
            <p>• You can re-subscribe at any time from this page.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelLoading}
              data-testid="cancel-keep-btn"
            >
              <X size={16} className="mr-1" /> Keep subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
              data-testid="cancel-confirm-btn"
            >
              {cancelLoading ? <><SpinnerGap size={16} className="mr-1 animate-spin" /> Canceling…</> : <><Stop size={16} className="mr-1" weight="fill" /> Confirm cancel</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Current Plan */}
      <Card className="shadow-card rounded-lg border-0 overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${PLAN_COLORS[currentPlanId] || '#7367F0'}15` }}>
              {React.createElement(PLAN_ICONS[currentPlanId] || CreditCard, { size: 28, style: { color: PLAN_COLORS[currentPlanId] || '#7367F0' }, weight: 'duotone' })}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold">Your Current Plan</h2>
                <Badge className="capitalize text-xs" style={{ backgroundColor: `${PLAN_COLORS[currentPlanId] || '#7367F0'}15`, color: PLAN_COLORS[currentPlanId] || '#7367F0' }}>{billing.plan_details.name}</Badge>
                {(() => {
                  const status = billing.subscription_status;
                  if (!status) return null;
                  const cfg = BILLING_STATUS_CFG[status] || { label: status.replace('_',' '), className: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20', Icon: Warning };
                  const Icon = cfg.Icon;
                  return (
                    <Badge className={`text-xs ${cfg.className}`} data-testid={`my-billing-status-${status}`}>
                      <Icon size={12} className="mr-1" /> {cfg.label}
                    </Badge>
                  );
                })()}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                ${billing.plan_details.price}/user/month
                {billing.total_monthly > 0 && <span className="font-semibold text-foreground ml-2">(${billing.total_monthly.toFixed(0)}/mo for {billing.usage.users} user{billing.usage.users !== 1 ? 's' : ''})</span>}
              </p>
            </div>
          </div>

          {/* Usage */}
          <div className="grid grid-cols-2 gap-8 mt-6">
            {[
              { label: 'Users', used: billing.usage.users, max: billing.plan_details.max_users },
              { label: 'Projects', used: billing.usage.projects, max: billing.plan_details.max_projects },
            ].map(item => {
              const pct = item.max >= 999999 ? 5 : Math.min(100, (item.used / item.max) * 100);
              const isNearLimit = item.max < 999999 && pct >= 80;
              return (
                <div key={item.label}>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="font-medium text-muted-foreground">{item.label}</span>
                    <span className={`font-bold ${isNearLimit ? 'text-amber-500' : ''}`}>{item.used} / {item.max >= 999999 ? 'Unlimited' : item.max}</span>
                  </div>
                  <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${isNearLimit ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                  </div>
                  {isNearLimit && <p className="text-[10px] text-amber-500 mt-1 font-medium">Approaching limit — consider upgrading</p>}
                </div>
              );
            })}
          </div>

          {/* Features */}
          {billing.plan_details.features?.length > 0 && (
            <div className="mt-6 pt-5 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Included Features</p>
              <div className="flex flex-wrap gap-2">
                {billing.plan_details.features.map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 text-xs bg-secondary/60 px-2.5 py-1 rounded-md">
                    <CheckCircle size={12} className="text-emerald-500" weight="fill" />{f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Plans */}
      <div>
        <h3 className="text-base font-semibold mb-4">Available Plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.filter(p => p.is_active !== false).map((plan, i) => {
            const Icon = PLAN_ICONS[plan.plan_id] || CreditCard;
            const color = PLAN_COLORS[plan.plan_id] || '#7367F0';
            const isCurrent = currentPlanId === plan.plan_id;
            const planOrder = plans.findIndex(p => p.plan_id === plan.plan_id);
            const isUpgrade = planOrder > currentPlanOrder;
            const isDowngrade = planOrder < currentPlanOrder;

            return (
              <Card key={plan.plan_id} className={`rounded-lg overflow-hidden transition-all ${isCurrent ? 'ring-2 ring-primary shadow-card' : 'border hover:shadow-card'}`} data-testid={`my-plan-${plan.plan_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                      <Icon size={20} style={{ color }} weight="duotone" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm">{plan.name}</h4>
                      {isCurrent && <Badge variant="outline" className="text-[9px] mt-0.5">Current</Badge>}
                    </div>
                  </div>

                  <div className="mb-3">
                    <span className="text-2xl font-bold">${plan.price?.toFixed(0)}</span>
                    <span className="text-xs text-muted-foreground">/user/mo</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {billing.usage.users} user{billing.usage.users !== 1 ? 's' : ''} = <span className="font-semibold text-foreground">${(plan.price * billing.usage.users).toFixed(0)}/mo</span>
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    {plan.max_projects >= 999999 ? 'Unlimited' : plan.max_projects} Projects, {plan.max_users >= 999999 ? 'Unlimited' : plan.max_users} Users
                  </p>
                  <ul className="space-y-1.5 mb-4">
                    {(plan.features || []).map((f, j) => <li key={j} className="flex items-center gap-1.5 text-xs"><CheckCircle size={12} className="text-emerald-500 shrink-0" weight="fill" />{f}</li>)}
                  </ul>

                  {isCurrent ? (
                    <Button variant="outline" className="w-full rounded-md text-xs h-9" disabled>Current Plan</Button>
                  ) : (
                    <Button onClick={() => handleCheckout(plan.plan_id)} disabled={!!checkoutLoading} className="w-full rounded-md text-xs h-9" style={isUpgrade ? { backgroundColor: color } : {}} variant={isDowngrade ? 'outline' : 'default'} data-testid={`checkout-my-${plan.plan_id}`}>
                      {checkoutLoading === plan.plan_id ? <SpinnerGap size={14} className="animate-spin mr-1.5" /> : isUpgrade ? <ArrowUp size={14} className="mr-1.5" weight="bold" /> : <ArrowDown size={14} className="mr-1.5" weight="bold" />}
                      {checkoutLoading === plan.plan_id ? 'Processing...' : isUpgrade ? 'Upgrade' : 'Downgrade'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Payment History */}
      {billing.transactions?.length > 0 && (
        <Card className="shadow-card rounded-lg border-0 overflow-hidden">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-sm font-bold flex items-center gap-2"><Receipt size={16} /> Payment History</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Plan</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Amount</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billing.transactions.map((tx, i) => (
                  <TableRow key={tx.id || i}>
                    <TableCell className="text-sm">{tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{tx.plan_id}</Badge></TableCell>
                    <TableCell className="text-sm font-medium">${tx.amount?.toFixed(2)}</TableCell>
                    <TableCell>
                      {tx.payment_status === 'paid' ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs"><CheckCircle size={12} className="mr-1" /> Paid</Badge>
                        : <Badge variant="secondary" className="text-xs capitalize">{tx.payment_status}</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
